import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/shared/store';

type UserManagementFormState = {
  userType: string;
  email: string;
  phone: string;
  roleId: string;
  enterpriseId?: number | null;
  errors: Record<string, string | undefined>;
};

type EditUserFormState = {
  email: string;
  roleId: number | null;
  enterpriseId: number | null;
  status: 'A' | 'I' | 'P';
};

type UserManagementState = {
  createUserForm: UserManagementFormState;
  editUserForm: EditUserFormState;
};

const initialState: UserManagementState = {
  createUserForm: {
    userType: '',
    email: '',
    phone: '',
    roleId: '',
    enterpriseId: null,
    errors: {},
  },
  editUserForm: {
    email: '',
    roleId: null,
    enterpriseId: null,
    status: 'A',
  },
};

const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    setUserType(state, action: PayloadAction<string>) {
      state.createUserForm.userType = action.payload;
    },
    setEmail(state, action: PayloadAction<string>) {
      state.createUserForm.email = action.payload;
    },
    setPhone(state, action: PayloadAction<string>) {
      state.createUserForm.phone = action.payload;
    },
    setRoleId(state, action: PayloadAction<string>) {
      state.createUserForm.roleId = action.payload;
    },
    setEnterpriseId(state, action: PayloadAction<number | null | undefined>) {
      state.createUserForm.enterpriseId = action.payload;
    },
    setErrors(
      state,
      action: PayloadAction<Record<string, string | undefined>>
    ) {
      state.createUserForm.errors = action.payload;
    },
    updateFieldError(
      state,
      action: PayloadAction<{ field: string; error: string | undefined }>
    ) {
      state.createUserForm.errors[action.payload.field] = action.payload.error;
    },
    resetCreateUserForm(state) {
      state.createUserForm = {
        userType: '',
        email: '',
        phone: '',
        roleId: '',
        enterpriseId: null,
        errors: {},
      };
    },
    // Edit User Form Actions
    setEditUserEmail(state, action: PayloadAction<string>) {
      state.editUserForm.email = action.payload;
    },
    setEditUserRoleId(state, action: PayloadAction<number | null>) {
      state.editUserForm.roleId = action.payload;
    },
    setEditUserEnterpriseId(state, action: PayloadAction<number | null>) {
      state.editUserForm.enterpriseId = action.payload;
    },
    setEditUserStatus(state, action: PayloadAction<'A' | 'I' | 'P'>) {
      state.editUserForm.status = action.payload;
    },
    setEditUserForm(state, action: PayloadAction<EditUserFormState>) {
      state.editUserForm = action.payload;
    },
    resetEditUserForm(state) {
      state.editUserForm = {
        email: '',
        roleId: null,
        enterpriseId: null,
        status: 'A',
      };
    },
  },
});

export const {
  setUserType,
  setEmail,
  setPhone,
  setRoleId,
  setEnterpriseId,
  setErrors,
  updateFieldError,
  resetCreateUserForm,
  setEditUserEmail,
  setEditUserRoleId,
  setEditUserEnterpriseId,
  setEditUserStatus,
  setEditUserForm,
  resetEditUserForm,
} = userManagementSlice.actions;

// Selectors
export const selectCreateUserForm = (state: RootState) =>
  state.userManagement.createUserForm;
export const selectUserType = (state: RootState) =>
  state.userManagement.createUserForm.userType;
export const selectEmail = (state: RootState) =>
  state.userManagement.createUserForm.email;
export const selectPhone = (state: RootState) =>
  state.userManagement.createUserForm.phone;
export const selectRoleId = (state: RootState) =>
  state.userManagement.createUserForm.roleId;
export const selectEnterpriseId = (state: RootState) =>
  state.userManagement.createUserForm.enterpriseId;
export const selectCreateUserErrors = (state: RootState) =>
  state.userManagement.createUserForm.errors;

// Edit User Selectors
export const selectEditUserForm = (state: RootState) =>
  state.userManagement.editUserForm;
export const selectEditUserEmail = (state: RootState) =>
  state.userManagement.editUserForm.email;
export const selectEditUserRoleId = (state: RootState) =>
  state.userManagement.editUserForm.roleId;
export const selectEditUserEnterpriseId = (state: RootState) =>
  state.userManagement.editUserForm.enterpriseId;
export const selectEditUserStatus = (state: RootState) =>
  state.userManagement.editUserForm.status;

export default userManagementSlice.reducer;
